import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Printer, Truck, Package, Archive, MapPin, ChevronDown, ChevronRight, ImageIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { hasExpress } from '../utils/express';
import { getImageUrl } from '../utils/imageUrl';
import { Checkbox } from '../components/ui/checkbox';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CheckinDashboard() {
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [archives, setArchives] = useState([]);
  const [printDialog, setPrintDialog] = useState(null);
  const [weight, setWeight] = useState('');
  const [numBoxes, setNumBoxes] = useState('1');
  const [addressDialog, setAddressDialog] = useState(null);
  const [shipConfirmOrder, setShipConfirmOrder] = useState(null);
  const [selectedForColis, setSelectedForColis] = useState({});
  const [colisDialogOpen, setColisDialogOpen] = useState(false);
  const [colisName, setColisName] = useState('');
  const [checkinPackages, setCheckinPackages] = useState([]);
  const [expandedPkg, setExpandedPkg] = useState({});
  const [imagePopupOpen, setImagePopupOpen] = useState(false);
  const [imagePopupImages, setImagePopupImages] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [ordRes, archRes, pkgRes] = await Promise.all([
        axios.get(`${API}/api/checkin/orders`, { withCredentials: true }),
        axios.get(`${API}/api/checkin/archives`, { withCredentials: true }),
        axios.get(`${API}/api/checkin/packages`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setOrders(ordRes.data);
      setArchives(archRes.data);
      setCheckinPackages(pkgRes.data || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePrintLabel = async () => {
    if (!printDialog || !weight) { toast.error(t('weight_required')); return; }
    try {
      const res = await axios.post(`${API}/api/checkin/orders/${printDialog._id}/delivery-label`,
        { weight: parseFloat(weight), num_boxes: parseInt(numBoxes) },
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Label_${printDialog.order_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(t('label_generated'));
      // Update local order data with saved values (don't close or reset)
      setOrders(prev => prev.map(o => o._id === printDialog._id ? { ...o, checkin_weight: parseFloat(weight), checkin_num_boxes: parseInt(numBoxes) } : o));
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleShip = async (orderOrPkg) => {
    try {
      if (orderOrPkg._pkg) {
        // Ship all orders in the package
        const ids = orderOrPkg._pkg_order_ids || [];
        await Promise.all(ids.map(id => axios.put(`${API}/api/checkin/orders/${id}/ship`, {}, { withCredentials: true })));
        toast.success(`Colis "${orderOrPkg._pkg_name}" expédié (${ids.length} commandes)`);
      } else {
        await axios.put(`${API}/api/checkin/orders/${orderOrPkg}/ship`, {}, { withCredentials: true });
        toast.success(t('order_shipped'));
      }
      setShipConfirmOrder(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const selectedColisIds = Object.keys(selectedForColis).filter(k => selectedForColis[k]);

  const handleCreateColis = async () => {
    if (!selectedColisIds.length || !colisName.trim()) return;
    try {
      await axios.post(`${API}/api/checkin/packages`, {
        order_ids: selectedColisIds, name: colisName.trim()
      }, { withCredentials: true });
      toast.success(`Colis "${colisName.trim()}" créé`);
      setColisDialogOpen(false);
      setSelectedForColis({});
      setColisName('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // Filter out fully-shipped packages (no active orders left)
  const activePackages = checkinPackages.filter(pkg =>
    (pkg.order_ids || []).some(oid => orders.find(x => x._id === oid))
  );

  // Group archives by month, then by package within each month
  const archiveGroups = {};
  archives.forEach(a => {
    const key = a.month_label || 'Autre';
    if (!archiveGroups[key]) archiveGroups[key] = [];
    archiveGroups[key].push(a);
  });

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Package size={24} className="text-slate-700" />
          <h1 className="text-2xl font-semibold text-slate-900">{t('checkin_title')}</h1>
          <Badge className="bg-amber-100 text-amber-800">{orders.filter(o => !o.transport_package_id).length} {t('pending')}</Badge>
        </div>

        {/* Active orders */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{t('checkin_orders')} ({orders.filter(o => !o.transport_package_id).length})</CardTitle>
              {selectedColisIds.length >= 1 && (
                <Button data-testid="checkin-create-colis-btn" className="bg-blue-600 hover:bg-blue-700 text-white gap-1 text-xs"
                  onClick={() => setColisDialogOpen(true)}>
                  <Package size={14} /> {t('create_colis')} ({selectedColisIds.length})
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              const freeOrders = orders.filter(o => !o.transport_package_id);
              return freeOrders.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">{t('no_checkin_orders')}</p>
              ) : (
                <div className="space-y-2">
                  {freeOrders.map(o => (
                  <div key={o._id} data-testid={`checkin-order-${o.order_number}`} className={`flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 ${o.transport_package_id ? 'border-blue-200 bg-blue-50/30' : 'border-slate-100'}`}>
                    {!o.transport_package_id && (
                      <Checkbox data-testid={`checkin-select-${o._id}`} checked={!!selectedForColis[o._id]} onCheckedChange={() => setSelectedForColis(prev => ({ ...prev, [o._id]: !prev[o._id] }))} />
                    )}
                    {o.transport_package_id && (
                      <Package size={14} className="text-blue-500 flex-shrink-0" />
                    )}
                    <div className="flex-shrink-0 cursor-pointer" onClick={() => { if (o.images && o.images.length > 0) { setImagePopupImages(o.images); setImagePopupOpen(true); } }}>
                      {o.images && o.images.length > 0 ? (
                        <img src={getImageUrl(o.images[0])} alt="" data-testid={`checkin-img-${o.order_number}`} className="w-12 h-12 object-cover rounded border border-slate-300 hover:opacity-80 transition-opacity"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                        />
                      ) : null}
                      <div className={`w-12 h-12 bg-slate-200 rounded border border-slate-300 items-center justify-center ${o.images && o.images.length > 0 ? 'hidden' : 'flex'}`}>
                        <ImageIcon size={16} className="text-slate-400" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm">{o.order_number}</span>
                        {hasExpress(o) && <span data-testid="express-badge" className="bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">EXPRESS</span>}
                        <span className="text-sm text-slate-700">{o.client_name}</span>
                        <Badge className="bg-slate-100 text-slate-600 text-xs">{o.client_country}</Badge>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" data-testid={`checkin-address-btn-${o.order_number}`} className="gap-1 text-xs" onClick={() => setAddressDialog(o)}>
                      <MapPin size={13} /> {t('address')}
                    </Button>
                    <Button size="sm" data-testid={`checkin-print-btn-${o.order_number}`} className="gap-1 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => { setPrintDialog(o); setWeight(o.checkin_weight != null ? String(o.checkin_weight) : ''); setNumBoxes(o.checkin_num_boxes != null ? String(o.checkin_num_boxes) : '1'); }}>
                      <Printer size={13} /> {t('print_delivery')}
                    </Button>
                    <Button size="sm" data-testid={`checkin-ship-btn-${o.order_number}`} className="gap-1 text-xs bg-blue-600 hover:bg-blue-700" onClick={() => setShipConfirmOrder(o)}>
                      <Truck size={13} /> {t('mark_shipped')}
                    </Button>
                  </div>
                ))}
              </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Created Packages */}
        {activePackages.length > 0 && (
          <Card className="border-blue-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Package size={18} className="text-blue-600" />
                <CardTitle className="text-base">{t('create_colis')} ({activePackages.length})</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {activePackages.map(pkg => {
                  const pkgOrders = (pkg.order_ids || []).map(oid => orders.find(x => x._id === oid)).filter(Boolean);
                  const allShipped = pkgOrders.length === 0;
                  return (
                  <div key={pkg._id} className="border border-blue-100 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 bg-blue-50">
                      <button
                        className="flex items-center gap-3 text-left hover:opacity-80"
                        onClick={() => setExpandedPkg(prev => ({ ...prev, [pkg._id]: !prev[pkg._id] }))}
                      >
                        {expandedPkg[pkg._id] ? <ChevronDown size={16} className="text-blue-500" /> : <ChevronRight size={16} className="text-blue-500" />}
                        <Package size={16} className="text-blue-600" />
                        <span className="font-bold text-blue-800 text-sm">{pkg.name}</span>
                        <span className="text-xs text-blue-600">({pkg.order_numbers?.length || 0} {t('colis_orders_count')})</span>
                      </button>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 mr-2">{(pkg.created_at || '').slice(0, 10)}</span>
                        {!allShipped && (
                          <>
                            <Button size="sm" className="gap-1 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => { setPrintDialog({ ...pkgOrders[0], _pkg_name: pkg.name, _pkg_orders: pkgOrders }); setWeight(''); setNumBoxes(String(pkgOrders.length)); }}>
                              <Printer size={13} /> {t('print_delivery')}
                            </Button>
                            <Button size="sm" className="gap-1 text-xs bg-blue-600 hover:bg-blue-700" onClick={() => setShipConfirmOrder({ _pkg: true, _pkg_name: pkg.name, _pkg_order_ids: pkg.order_ids, _pkg_orders: pkgOrders })}>
                              <Truck size={13} /> {t('mark_shipped')}
                            </Button>
                          </>
                        )}
                        {allShipped && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{t('mark_shipped')}</Badge>}
                      </div>
                    </div>
                    {expandedPkg[pkg._id] && (
                      <div className="px-4 py-2 bg-white border-t border-blue-100 space-y-2">
                        {(pkg.order_ids || []).map((oid, i) => {
                          const o = orders.find(x => x._id === oid);
                          const orderNum = (pkg.order_numbers || [])[i] || oid;
                          if (!o) {
                            return (
                              <div key={oid} className="flex items-center gap-3 py-2 px-2 rounded bg-slate-50 border border-slate-100">
                                <span className="text-xs text-slate-500 flex-1">{orderNum}</span>
                                <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{t('mark_shipped')}</Badge>
                              </div>
                            );
                          }
                          return (
                            <div key={oid} className="flex items-center gap-3 py-2 px-2 rounded border border-slate-100 hover:bg-slate-50">
                              <span className="text-sm font-medium flex-1">{o.order_number} <span className="text-slate-500 font-normal">{o.client_name}</span></span>
                              <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={() => setAddressDialog(o)}>
                                <MapPin size={13} /> {t('address')}
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Archives */}
        {Object.keys(archiveGroups).length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
              <Archive size={18} className="text-slate-500" />
              <h2 className="text-lg font-semibold text-slate-700">{t('designer_archives_title')} ({archives.length})</h2>
            </div>
            <Accordion type="multiple" className="space-y-2">
              {Object.entries(archiveGroups).map(([month, monthOrders]) => (
                <AccordionItem key={month} value={month} className="border border-slate-200 rounded-lg overflow-hidden">
                  <AccordionTrigger className="hover:no-underline px-4 py-3 bg-slate-50 hover:bg-slate-100">
                    <div className="flex items-center justify-between w-full pr-2">
                      <span className="text-sm font-semibold text-slate-700">{month}</span>
                      <Badge className="bg-emerald-100 text-emerald-800 text-xs">{monthOrders.length}</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="divide-y divide-slate-100">
                      {(() => {
                        // Group by package name within the month
                        const pkgGroups = {};
                        const standalone = [];
                        monthOrders.forEach(a => {
                          if (a.transport_package_name) {
                            if (!pkgGroups[a.transport_package_name]) pkgGroups[a.transport_package_name] = [];
                            pkgGroups[a.transport_package_name].push(a);
                          } else {
                            standalone.push(a);
                          }
                        });
                        return (
                          <>
                            {Object.entries(pkgGroups).map(([pkgName, pkgOrders]) => (
                              <div key={pkgName} className="border-l-4 border-blue-300 bg-blue-50/30">
                                <div className="flex items-center gap-2 px-4 py-2">
                                  <Package size={14} className="text-blue-500" />
                                  <span className="text-xs font-bold text-blue-700">{pkgName}</span>
                                  <span className="text-[10px] text-blue-500">({pkgOrders.length})</span>
                                </div>
                                {pkgOrders.map(a => (
                                  <div key={a._id} className="flex items-center gap-3 py-1.5 px-4 pl-8 text-sm">
                                    <span className="text-xs text-slate-400 w-20">{a.order_date ? new Date(a.order_date).toLocaleDateString('fr-FR') : ''}</span>
                                    <span className="font-medium flex-1">
                                      {a.order_number}
                                      {hasExpress(a) && <span data-testid="express-badge" className="ml-2 inline-block bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">EXPRESS</span>}
                                    </span>
                                    <span className="text-slate-600">{a.client_name}</span>
                                    <Badge className="bg-slate-100 text-slate-600 text-xs">{a.client_country}</Badge>
                                  </div>
                                ))}
                              </div>
                            ))}
                            {standalone.map(a => (
                              <div key={a._id} className="flex items-center gap-3 py-2 px-4 text-sm">
                                <span className="text-xs text-slate-400 w-20">{a.order_date ? new Date(a.order_date).toLocaleDateString('fr-FR') : ''}</span>
                                <span className="font-medium flex-1">
                                  {a.order_number}
                                  {hasExpress(a) && <span data-testid="express-badge" className="ml-2 inline-block bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">EXPRESS</span>}
                                </span>
                                <span className="text-slate-600">{a.client_name}</span>
                                <Badge className="bg-slate-100 text-slate-600 text-xs">{a.client_country}</Badge>
                                <Badge className="bg-emerald-100 text-emerald-800 text-xs">{a.status}</Badge>
                              </div>
                            ))}
                          </>
                        );
                      })()}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}

        {/* Print Delivery Dialog */}
        <Dialog open={!!printDialog} onOpenChange={(open) => { if (!open) setPrintDialog(null); }}>
          <DialogContent data-testid="checkin-print-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Printer size={18} /> {t('print_delivery')}</DialogTitle>
            </DialogHeader>
            {printDialog && (
              <div className="space-y-4">
                <div className="bg-slate-50 rounded p-3 text-sm">
                  <p><b>{printDialog.order_number}</b> &mdash; {printDialog.client_name}</p>
                  <p className="text-slate-500">{[printDialog.client_address, [printDialog.client_postal_code, printDialog.client_city].filter(Boolean).join(' '), printDialog.client_country].filter(Boolean).join(', ')}</p>
                  <p className="text-slate-500">{printDialog.num_pieces} {printDialog.piece_type}</p>
                </div>
                <div>
                  <Label className="text-sm font-semibold">{t('weight_kg')} *</Label>
                  <Input type="number" step="0.1" min="0.1" value={weight} onChange={e => setWeight(e.target.value)} placeholder="Ex: 5.2" data-testid="label-weight" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">{t('number_of_boxes')} *</Label>
                  <Select value={numBoxes} onValueChange={setNumBoxes}>
                    <SelectTrigger data-testid="label-boxes">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 50 }, (_, i) => i + 1).map(n => (
                        <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" data-testid="checkin-print-cancel" onClick={() => setPrintDialog(null)}>{t('cancel')}</Button>
                  <Button className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="checkin-generate-label" onClick={handlePrintLabel}>
                    <Printer size={14} /> {t('generate_label')}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Address Dialog */}
        <Dialog open={!!addressDialog} onOpenChange={(open) => { if (!open) setAddressDialog(null); }}>
          <DialogContent data-testid="checkin-address-dialog">
            <DialogHeader><DialogTitle>{t('address')}</DialogTitle></DialogHeader>

            {addressDialog && (
              <div className="space-y-3 text-sm">
                <div><Label className="text-xs text-slate-500">{t('client')}</Label><p className="font-medium">{addressDialog.client_name} {addressDialog.client_firstname}</p></div>
                <div><Label className="text-xs text-slate-500">{t('address')}</Label><p className="font-medium">{addressDialog.client_address || '—'}</p></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs text-slate-500">{t('postal_code')}</Label><p className="font-medium">{addressDialog.client_postal_code || '—'}</p></div>
                  <div><Label className="text-xs text-slate-500">{t('city')}</Label><p className="font-medium">{addressDialog.client_city || '—'}</p></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs text-slate-500">{t('country')}</Label><p className="font-medium">{addressDialog.client_country}</p></div>
                  <div><Label className="text-xs text-slate-500">{t('phone')}</Label><p className="font-medium">{addressDialog.client_phone || '—'}</p></div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Ship Confirmation AlertDialog */}
        <AlertDialog open={!!shipConfirmOrder} onOpenChange={(open) => { if (!open) setShipConfirmOrder(null); }}>
          <AlertDialogContent data-testid="checkin-ship-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('confirm_ship')}</AlertDialogTitle>
              <AlertDialogDescription>
                {shipConfirmOrder && shipConfirmOrder._pkg ? (
                  <span>Colis "{shipConfirmOrder._pkg_name}" — {shipConfirmOrder._pkg_orders?.length || 0} commande(s)</span>
                ) : shipConfirmOrder && (
                  <span>{shipConfirmOrder.order_number} — {shipConfirmOrder.client_name}</span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="checkin-ship-cancel">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction
                data-testid="checkin-ship-confirm"
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => handleShip(shipConfirmOrder?._pkg ? shipConfirmOrder : shipConfirmOrder?._id)}
              >
                <Truck size={14} className="mr-1" /> {t('mark_shipped')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Create Colis Dialog */}
        <Dialog open={colisDialogOpen} onOpenChange={setColisDialogOpen}>
          <DialogContent data-testid="checkin-colis-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Package size={18} /> {t('create_colis')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-semibold">{t('colis_name')}</Label>
                <Input data-testid="checkin-colis-name-input" value={colisName} onChange={e => setColisName(e.target.value)} placeholder="Ex: Lot France Août 2026" className="mt-1" />
              </div>
              <div className="text-xs text-slate-500">
                <p className="font-semibold mb-1">{selectedColisIds.length} {t('colis_orders_count')} :</p>
                {selectedColisIds.map(id => {
                  const o = orders.find(x => x._id === id);
                  return <div key={id} className="py-0.5">{o ? o.order_number : id}</div>;
                })}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setColisDialogOpen(false)}>{t('cancel')}</Button>
                <Button data-testid="checkin-colis-confirm-btn" className="bg-blue-600 hover:bg-blue-700 gap-1" onClick={handleCreateColis} disabled={!colisName.trim()}>
                  <Package size={14} /> {t('create_colis')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Image Popup */}
        <Dialog open={imagePopupOpen} onOpenChange={setImagePopupOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Photos de la commande</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {imagePopupImages.map((img, i) => (
                <img key={i} src={getImageUrl(img)} alt={`Photo ${i + 1}`} className="w-full rounded-lg border border-slate-200" />
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
